import { CalendarDays, Check, Download, Loader2, MapPin } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVoucherStore } from '../stores/voucherStore';
import { useCampaignStore } from '../stores/campaignStore';
import { customerService } from '../services/customerService';
import { reservationService } from '../services/reservationService';
import { downloadVoucherPdf } from '../utils/downloadVoucherPdf';
import { StepProgress } from '../components/StepProgress';
import { CampaignHeader } from '../components/CampaignHeader';
import { useAnalytics } from '../hooks/useAnalytics';

const MONTHS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const WEEKDAYS = [
  'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
  'quinta-feira', 'sexta-feira', 'sábado',
];

function formatDate(value?: string) {
  if (!value) return '—';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getDate()}/${MONTHS[date.getMonth()]} (${WEEKDAYS[date.getDay()]})`;
}

function formatCpf(value?: string) {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return value;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function Step4Confirmation() {
  const navigate = useNavigate();
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { customer, reservation, reset } = useVoucherStore();
  const { campaign } = useCampaignStore();
  const { trackException, trackSelectContent } = useAnalytics();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  const submissionStarted = useRef(false);
  const customerRegistered = useRef(false);

  const persistReservation = async () => {
    if (submissionStarted.current) return;
    submissionStarted.current = true;
    setStatus('loading');
    setError('');

    // If voucherCode already exists, this is a "consult existing voucher" flow — skip creation
    if (reservation?.voucherCode) {
      setStatus('success');
      return;
    }

    if (!customer || !reservation?.storeId || !reservation.scheduledDate || !campaign?.id) {
      trackException('reservation_incomplete_data');
      submissionStarted.current = false;
      setStatus('error');
      setError('Não foi possível concluir a reserva porque os dados estão incompletos.');
      return;
    }

    try {
      if (!customerRegistered.current) {
        await customerService.register({
          name: customer.name || '',
          cpf: customer.cpf || '',
          email: customer.email || '',
          phone: customer.phone || '',
          birthDate: customer.birthDate || '',
          state: customer.state || '',
          city: customer.city || '',
          consentLgpd: customer.consentLgpd === true,
          consentTerms: customer.consentTerms === true,
          consentNotifications: customer.consentNotifications === true,
        });
        customerRegistered.current = true;
      }

      await reservationService.create({
        campaignId: campaign.id,
        storeId: reservation.storeId,
        redemptionDate: reservation.scheduledDate,
      });
      setStatus('success');
    } catch (submissionError) {
      console.error('Erro ao persistir cadastro e reserva:', submissionError);
      trackException('reservation_persistence_failed');
      submissionStarted.current = false;
      setStatus('error');
      setError('Não foi possível concluir sua reserva. Tente novamente.');
    }
  };

  useEffect(() => {
    void persistReservation();
  }, [campaign?.id, customer, reservation]);

  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    trackSelectContent('button', 'baixar_voucher');
    setDownloading(true);
    try {
      await downloadVoucherPdf('voucher-content', `voucher-${reservation?.voucherCode || 'riachuelo'}.pdf`);
    } catch {
      trackException('voucher_download_failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleNewReservation = () => {
    trackSelectContent('button', 'nova_reserva');
    reset();
    navigate(`/${campaignSlug}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CampaignHeader />
      <div className="max-w-lg mx-auto w-full px-6 pt-2">
        <StepProgress currentStep={4} />
      </div>

      <main id="voucher-content" className="max-w-lg mx-auto w-full px-5 pt-16 pb-6 flex-1">
        {status === 'loading' && (
          <section className="text-center" aria-live="polite">
            <Loader2 size={42} className="mx-auto animate-spin text-rchlo-green" />
            <h1 className="mt-5 text-2xl font-bold text-gray-950">Finalizando sua reserva...</h1>
          </section>
        )}

        {status === 'error' && (
          <section className="text-center" aria-live="assertive">
            <h1 className="text-2xl font-bold text-gray-950">Não foi possível concluir</h1>
            <p className="mt-3 text-gray-600">{error}</p>
            <button type="button" onClick={() => void persistReservation()} className="btn-primary mt-6 w-full">
              Tentar novamente
            </button>
          </section>
        )}

        {status === 'success' && <section className="text-center">
          <div className="mx-auto flex h-11 w-24 items-center justify-center rounded-full bg-rchlo-green text-white shadow-sm">
            <Check size={34} strokeWidth={3} />
          </div>
          <h1 className="mt-5 text-3xl font-bold text-gray-950">Parabéns!</h1>
          <p className="mt-2 text-xl text-gray-900">Você reservou seu brinde!</p>
        </section>}

        {status === 'success' && <section className="mt-8 rounded-2xl border border-gray-100 bg-gray-50/80 px-5 py-6">
          <h2 className="text-sm font-bold tracking-wide text-gray-500">RESUMO DA RESERVA</h2>
          <div className="mt-3 divide-y divide-gray-200">
            <SummaryRow icon={<CalendarDays size={18} />} label="CPF" value={formatCpf(customer?.cpf)} />
            <SummaryRow
              icon={<MapPin size={18} />}
              label="LOJA"
              value={[reservation?.storeName, reservation?.storeCity, reservation?.storeState]
                .filter(Boolean)
                .join(' • ') || '—'}
            />
            <SummaryRow icon={<CalendarDays size={18} />} label="DATA DE RESGATE" value={formatDate(reservation?.scheduledDate)} />
          </div>
        </section>}

        {status === 'success' && <section className="mt-7 rounded-2xl border border-sky-200 bg-sky-50 px-5 py-6 text-blue-800">
          <h2 className="text-base font-bold">Instruções para Resgate:</h2>
          <p className="mt-3 text-sm leading-6">
            Compareça na loja e dia escolhidos, leve seu documento com foto e apresente no atendimento para resgatar seu brinde!
          </p>
        </section>}
      </main>

      <footer className="border-t border-gray-100 px-5 py-4">
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={downloading || status !== 'success'}
          className="btn-primary mx-auto flex w-full max-w-lg items-center justify-center gap-3 text-base uppercase tracking-wide"
        >
          {downloading ? <Loader2 size={19} className="animate-spin" /> : <Download size={19} />}
          {downloading ? 'Gerando PDF...' : 'Baixar voucher'}
        </button>
        <button type="button" onClick={handleNewReservation} className="sr-only">
          Fazer nova reserva
        </button>
      </footer>
    </div>
  );
}

function SummaryRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e5eee9] text-rchlo-green">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-xs text-gray-400">{label}</span>
        <span className="block break-words text-base text-gray-950">{value}</span>
      </span>
    </div>
  );
}

